import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SERVICES } from '@/data/site';
import { SERVICE_DIRECTORY } from '@/data/services';
import { getEcosystemItems } from '@/data/ecosystem';
import { useLiveTick } from '@/lib/liveContent';
import { Reveal } from '../Reveal';

// Resolve a directory entry: catalogue slugs take name + route from the
// Ecosystem data source (no duplicated names); other entries link to the
// relevant division page until dedicated detail pages exist.
const resolveItem = (entry, divisionHref) => {
  if (entry.slug) {
    const eco = getEcosystemItems().find((it) => it.slug === entry.slug);
    if (eco) return { name: eco.name, href: eco.href };
  }
  return { name: entry.name, href: entry.href || divisionHref };
};

// Section 4 — compact service directory, four labelled groups.
export const ServicesDirectory = () => {
  useLiveTick();
  return (
  <section aria-labelledby="directory-heading" data-testid="services-directory" className="bg-mist py-16 sm:py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <p className="flex items-center gap-3">
          <span className="font-mono text-xs font-medium tracking-widest text-gold-700">04</span>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">Directory</span>
        </p>
        <h2 id="directory-heading" data-testid="directory-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
          Services at a glance
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
          Explore individual services, packages and programmes across Amden Group.
        </p>
      </div>

      <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICE_DIRECTORY.map((group, gi) => {
          const division = SERVICES.find((s) => s.id === group.division);
          return (
            <Reveal key={group.division} delay={gi * 0.06} className="h-full">
              <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6" data-testid={`directory-${group.division}`}>
                <h3
                  className="border-b-2 pb-3 text-sm font-extrabold uppercase tracking-[0.14em]"
                  style={{ borderColor: division.color, color: division.color }}
                >
                  {division.name.replace('Amden ', '')}
                </h3>
                <ul className="mt-4 flex-1 space-y-1">
                  {group.items.map((entry) => {
                    const item = resolveItem(entry, division.href);
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          data-testid={`directory-link-${(entry.slug || entry.name).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          className="group/link flex items-center justify-between gap-2 rounded-md px-2 py-2 text-sm font-semibold text-navy-800 transition-colors hover:bg-navy-50 hover:text-navy-900"
                        >
                          {item.name}
                          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-300 transition-all duration-200 group-hover/link:translate-x-0.5 group-hover/link:text-navy-900" aria-hidden="true" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
};
